import { useCallback, useMemo, useRef, useState } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  AiAgentContinueResponse,
  AiAgentToolCall,
  AiAgentToolResult,
  AiChatMessage,
  MetricComparisonResponse,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import {
  useCfoAgent,
  type CfoAgentBaseRequest,
  type CfoAgentSessionResolver,
  type CfoAgentToolHandlers,
} from '../../../ai/cfoAgent'
import { buildCfoBriefToolHandlers } from '../../../ai/cfoBriefToolHandlers'
import { copilotModelId, copilotProviderForAiRequest } from '../../../ai/settings'

const trimStr = (value: unknown): string => String(value ?? '').trim()

const PAGE_AI_MAX_TOKENS = 950
const PAGE_AI_REASONING_EFFORT = 'low'

export type CfoCopilotPromptChip = { label: string; prompt: string }

export type UseCfoCopilotConfig = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null | undefined
  /**
   * Page-aware section tag threaded into every agent turn (`active_section`), so
   * the copilot knows which page it is reading (e.g. `cfo_financials`). This is
   * how the shared (generalized) tool bundle becomes page-aware without forking
   * a separate handler implementation per page.
   */
  activeSection: string
  /** DuckDB opts for analytic tools (per-tab execution scope). */
  duckDbOpts?: Record<string, any> | null
  /** Live comparison snapshot the page already rendered, reused for grounding. */
  getComparisons?: () => MetricComparisonResponse | null
  /** Optional analytic worker session resolver. */
  resolveAnalysisSession?: CfoAgentSessionResolver
  /** Default suggested prompts shown until the agent emits followups. */
  suggestedPrompts?: string[]
  /** Extra page-specific tool handlers merged over the shared bundle. */
  extraToolHandlers?: CfoAgentToolHandlers
  /**
   * Advanced desktop/ad hoc mode only. Normal CFO page copilots keep browser
   * DuckDB tools disabled so answers use governed server contracts.
   */
  allowLocalDuckDbTools?: boolean
  /**
   * Staged progress-card builder. When provided, deterministic evidence
   * (charts/tables) from each tool result is rendered inline BEFORE the model's
   * final answer — the guided-analysis experience. A fresh dedupe set is created
   * per turn. Defaults to off (plain text stream).
   */
  buildProgressMessages?: (
    result: AiAgentToolResult,
    stages: Set<string>,
    context: { call: AiAgentToolCall; agent_run_id: string; tool_results: AiAgentToolResult[] },
  ) => AiChatMessage[] | void
  /** Invoked with the final structured answer once a turn completes. */
  onAnswer?: (response: AiAgentContinueResponse) => void
}

export type UseCfoCopilotResult = {
  messages: ReturnType<typeof useCfoAgent>['messages']
  response: ReturnType<typeof useCfoAgent>['response']
  busy: boolean
  error: string
  input: string
  setInput: (value: string) => void
  sendMessage: (text: string, overrides?: Partial<CfoAgentBaseRequest>) => Promise<void>
  promptChips: CfoCopilotPromptChip[]
  reset: () => void
}

export const CFO_COPILOT_LOCAL_DUCKDB_TOOL_NAMES = [
  'run_duckdb_sql',
  'run_duckdb_plan',
  'save_agent_result_table',
  'groupby',
  'topk',
  'correlation',
  'outliers',
  'list_agent_artifacts',
  'run_drilldown_recipe',
  'get_opex_breakdown',
  'get_gl_anomaly_detail',
  'get_metric_definition',
  'get_metric_trend',
  'explain_metric_change',
  'get_statement_detail',
] as const

const localDuckDbToolBlocked = async (_args: Record<string, any>, call: AiAgentToolCall) => ({
  status: 'not_ready',
  summary: `The normal CFO page copilot cannot use browser DuckDB tool "${call.name}". Ask through governed CFO page contracts, metric evidence, or bounded GL summary; use the guided analysis workspace for local/ad hoc exploration.`,
  blocked_tool: call.name,
  policy: 'server_first_cfo_page_contracts',
  allowed_server_tools: [
    'list_cfo_page_contracts',
    'get_cfo_page_contract',
    'get_cfo_metric_evidence',
    'get_insights',
    'search_cfo_gl_summary',
  ],
})

export const buildCfoCopilotToolHandlers = (
  shared: CfoAgentToolHandlers,
  extraToolHandlers?: CfoAgentToolHandlers,
  opts?: { allowLocalDuckDbTools?: boolean },
): CfoAgentToolHandlers => {
  const blockedHandlers: CfoAgentToolHandlers = {}
  if (!opts?.allowLocalDuckDbTools) {
    for (const name of CFO_COPILOT_LOCAL_DUCKDB_TOOL_NAMES) {
      blockedHandlers[name] = localDuckDbToolBlocked
    }
  }
  return { ...shared, ...blockedHandlers, ...(extraToolHandlers || {}) }
}

/**
 * Page-aware copilot hook. Wraps `useCfoAgent` with the shared, generalized CFO
 * tool bundle (`buildCfoBriefToolHandlers`, which routes to the canonical
 * metric/ratio/statement engines) and threads a per-page `active_section` into
 * every turn. One implementation per feature: each page reuses this hook instead
 * of re-deriving copilot wiring.
 */
export function useCfoCopilot(config: UseCfoCopilotConfig): UseCfoCopilotResult {
  const {
    appApi,
    workerClient = null,
    workbookId,
    activeSection,
    duckDbOpts = null,
    getComparisons,
    resolveAnalysisSession,
    suggestedPrompts = [],
    extraToolHandlers,
    allowLocalDuckDbTools = false,
    buildProgressMessages,
    onAnswer,
  } = config

  const buildProgressRef = useRef(buildProgressMessages)
  buildProgressRef.current = buildProgressMessages
  const onAnswerRef = useRef(onAnswer)
  onAnswerRef.current = onAnswer

  // WS1: the CFO page copilot runs Opus 4.8 (Bedrock). No images / no web search on this surface.
  const aiRequestProvider = copilotProviderForAiRequest()
  const aiModelId = copilotModelId()

  const comparisonsGetterRef = useRef(getComparisons)
  comparisonsGetterRef.current = getComparisons

  const toolHandlers = useMemo<CfoAgentToolHandlers>(() => {
    const shared = buildCfoBriefToolHandlers({
      appApi,
      workerClient,
      duckDbOpts,
      workbookId,
      getComparisons: () => comparisonsGetterRef.current?.() ?? null,
    })
    return buildCfoCopilotToolHandlers(shared, extraToolHandlers, { allowLocalDuckDbTools })
  }, [appApi, workerClient, duckDbOpts, workbookId, extraToolHandlers, allowLocalDuckDbTools])

  const log = useCallback(
    (event: string, payload?: Record<string, any>) => {
      try {
        console.debug(`[cfo-copilot:${activeSection}]`, event, payload || {})
      } catch {}
    },
    [activeSection],
  )

  const agent = useCfoAgent({
    appApi,
    workerClient,
    workbookId,
    toolHandlers,
    resolveAnalysisSession,
    log,
  })

  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const sendMessage = useCallback(
    async (text: string, overrides?: Partial<CfoAgentBaseRequest>) => {
      const content = trimStr(text)
      if (!content || busy) return
      setBusy(true)
      setError('')
      setInput('')
      try {
        const baseRequest = {
          provider: aiRequestProvider,
          model_id: aiRequestProvider ? aiModelId || null : null,
          workbook_id: workbookId || null,
          active_section: activeSection,
          max_tokens: PAGE_AI_MAX_TOKENS,
          reasoning_effort: PAGE_AI_REASONING_EFFORT,
          output_targets: [],
          ...(overrides || {}),
        }
        const progressiveStages = new Set<string>()
        await agent.sendMessage(content, baseRequest, {
          onAnswer: (response) => onAnswerRef.current?.(response),
          onToolResult: (result, ctx) => buildProgressRef.current?.(result, progressiveStages, ctx) || undefined,
        })
      } catch (err: any) {
        setError(trimStr(err?.message || err) || 'The copilot could not answer that.')
      } finally {
        setBusy(false)
      }
    },
    [activeSection, agent, aiModelId, aiRequestProvider, busy, workbookId],
  )

  const promptChips = useMemo<CfoCopilotPromptChip[]>(() => {
    const followups = (agent.response?.followups || [])
      .map((f) => ({ label: trimStr(f.label), prompt: trimStr(f.prompt) }))
      .filter((f) => f.label && f.prompt)
      .slice(0, 3)
    if (followups.length) return followups
    return suggestedPrompts.map((prompt) => ({ label: prompt, prompt }))
  }, [agent.response, suggestedPrompts])

  return {
    messages: agent.messages,
    response: agent.response,
    busy,
    error,
    input,
    setInput,
    sendMessage,
    promptChips,
    reset: agent.reset,
  }
}
